import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { ACHIEVEMENTS, ACHIEVEMENTS_BY_ID, totalPoints } from "../data/achievements";
import { computeLevel, XP_REWARDS } from "../data/profile";
import { SFX } from "../utils/audio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GUEST_KEY = "checkpoint_guest_profile_v1";

const DEFAULT_GUEST = {
  display_name: "Aventurier",
  avatar_classe: null,
  rank_points: 0,
  xp_total: 0,
  achievements: [],
  cosmetics_owned: [],
  equipped_border: "default",
  equipped_banner: "default",
  meta_gold_earned: 0,
  meta_gold_spent: 0,
};

const ProfileContext = createContext(null);

const loadGuest = () => {
  try {
    const raw = localStorage.getItem(GUEST_KEY);
    return raw ? { ...DEFAULT_GUEST, ...JSON.parse(raw) } : { ...DEFAULT_GUEST };
  } catch (e) {
    return { ...DEFAULT_GUEST };
  }
};

const saveGuest = (p) => {
  try { localStorage.setItem(GUEST_KEY, JSON.stringify(p)); } catch (e) {}
};

export const ProfileProvider = ({ children }) => {
  const { user, refreshUser } = useAuth();
  const [guest, setGuest] = useState(loadGuest);
  const guestRef = useRef(guest);
  useEffect(() => { guestRef.current = guest; saveGuest(guest); }, [guest]);

  // Unified read: prefer authenticated user fields if present, else guest
  const profile = useMemo(() => {
    const src = user || guest;
    const xp_total = src?.xp_total ?? 0;
    const level = src?.level ?? computeLevel(xp_total).level;
    const lvlInfo = computeLevel(xp_total);
    return {
      authed: !!user,
      user_id: user?.user_id || "guest",
      name: user?.name || "Invité",
      picture: user?.picture || null,
      display_name: src?.display_name || user?.name || "Aventurier",
      avatar_classe: src?.avatar_classe || null,
      rank_points: src?.rank_points || 0,
      xp_total,
      level,
      currentXP: lvlInfo.currentXP,
      neededXP: lvlInfo.neededXP,
      progress: lvlInfo.progress,
      achievements: src?.achievements || [],
      friend_code: user?.friend_code || null,
      cosmetics_owned: src?.cosmetics_owned || [],
      equipped_border: src?.equipped_border || "default",
      equipped_banner: src?.equipped_banner || "default",
      meta_gold_earned: src?.meta_gold_earned || 0,
      meta_gold_spent: src?.meta_gold_spent || 0,
      meta_gold_available: Math.max(0, (src?.meta_gold_earned || 0) - (src?.meta_gold_spent || 0)),
    };
  }, [user, guest]);

  // ============ XP ============
  const grantXP = useCallback(async (amount, reason) => {
    if (!amount || amount <= 0) return;
    if (user) {
      try {
        const res = await axios.post(`${API}/profile/xp`, { amount, reason }, { withCredentials: true });
        if (res.data?.leveled_up) {
          SFX.victory?.();
          toast.success(`🆙 Niveau ${res.data.level} atteint !`, { description: `+${amount} XP (${reason || "récompense"})`, duration: 3500 });
        } else {
          toast(`+${amount} XP`, { description: reason || "Bien joué !", duration: 1800 });
        }
        await refreshUser();
      } catch (e) {}
    } else {
      // Guest mode: keep XP locally
      const before = computeLevel(guestRef.current.xp_total).level;
      const newTotal = (guestRef.current.xp_total || 0) + amount;
      const after = computeLevel(newTotal).level;
      setGuest((g) => ({ ...g, xp_total: newTotal }));
      if (after > before) {
        SFX.victory?.();
        toast.success(`🆙 Niveau ${after} atteint !`, { description: `+${amount} XP (${reason || "récompense"})`, duration: 3500 });
      } else {
        toast(`+${amount} XP`, { description: reason || "Bien joué !", duration: 1800 });
      }
    }
  }, [user, refreshUser]);

  // ============ ACHIEVEMENTS ============
  const isUnlocked = useCallback((id) => {
    return (profile.achievements || []).some((a) => a.id === id);
  }, [profile.achievements]);

  const unlockAchievement = useCallback(async (id) => {
    const def = ACHIEVEMENTS_BY_ID[id];
    if (!def) return false;
    if (isUnlocked(id)) return false;

    // Optimistic UI feedback
    toast.success(`🏆 Succès débloqué !`, {
      description: `${def.icon} ${def.name} (+${def.points} XP)`,
      duration: 4000,
    });
    SFX.coin?.();

    if (user) {
      try {
        await axios.post(`${API}/profile/achievements`, { achievement_id: id, points: def.points }, { withCredentials: true });
        await refreshUser();
      } catch (e) {}
    } else {
      setGuest((g) => {
        if ((g.achievements || []).some((a) => a.id === id)) return g;
        return {
          ...g,
          achievements: [...(g.achievements || []), { id, unlocked_at: new Date().toISOString() }],
          // Achievement points go fully into XP (no rank points outside PvP)
          xp_total: (g.xp_total || 0) + def.points,
        };
      });
    }
    return true;
  }, [user, refreshUser, isUnlocked]);

  // Méta-succès de collection (se débloquent automatiquement selon le nombre de succès)
  useEffect(() => {
    const count = (profile.achievements || []).length;
    if (count >= 30) unlockAchievement("the_collector");
    if (count >= 60) unlockAchievement("trophy_hunter");
    if (count >= ACHIEVEMENTS.length - 1) unlockAchievement("the_completionist2");
  }, [profile.achievements, unlockAchievement]);

  // ============ PROFILE UPDATES ============
  const updateProfile = useCallback(async ({ avatar_classe, display_name }) => {
    if (user) {
      try {
        await axios.patch(`${API}/profile/me`, { avatar_classe, display_name }, { withCredentials: true });
        await refreshUser();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur de mise à jour");
      }
    } else {
      setGuest((g) => ({
        ...g,
        avatar_classe: avatar_classe !== undefined ? avatar_classe : g.avatar_classe,
        display_name: display_name !== undefined ? display_name : g.display_name,
      }));
    }
  }, [user, refreshUser]);

  // ============ COSMETICS / META GOLD ============
  const grantMetaGold = useCallback(async (amount) => {
    if (!amount || amount <= 0) return;
    if (user) {
      try {
        await axios.post(`${API}/profile/meta-gold`, { amount }, { withCredentials: true });
        await refreshUser();
      } catch (e) {}
    } else {
      setGuest((g) => ({ ...g, meta_gold_earned: (g.meta_gold_earned || 0) + amount }));
    }
  }, [user, refreshUser]);

  const purchaseBorder = useCallback(async (cosmeticId, price) => {
    const owned = profile.cosmetics_owned || [];
    if (owned.includes(cosmeticId)) {
      toast.info("Tu possèdes déjà ce cosmétique");
      return false;
    }
    if (profile.meta_gold_available < price) {
      toast.error("Pas assez d'or", { description: `Il te manque ${price - profile.meta_gold_available} or` });
      return false;
    }
    if (user) {
      try {
        await axios.post(`${API}/profile/cosmetic/purchase`, { cosmetic_id: cosmeticId, price }, { withCredentials: true });
        await refreshUser();
        SFX.coin?.();
        toast.success("Achat réussi !", { description: `-${price} or` });
        return true;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Achat impossible");
        return false;
      }
    } else {
      setGuest((g) => ({
        ...g,
        cosmetics_owned: [...(g.cosmetics_owned || []), cosmeticId],
        meta_gold_spent: (g.meta_gold_spent || 0) + price,
      }));
      SFX.coin?.();
      toast.success("Achat réussi !", { description: `-${price} or` });
      return true;
    }
  }, [user, refreshUser, profile.cosmetics_owned, profile.meta_gold_available]);

  const equipBorder = useCallback(async (borderId) => {
    if (user) {
      try {
        await axios.post(`${API}/profile/cosmetic/equip`, { border_id: borderId }, { withCredentials: true });
        await refreshUser();
        SFX.click?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur");
      }
    } else {
      setGuest((g) => ({ ...g, equipped_border: borderId }));
      SFX.click?.();
    }
  }, [user, refreshUser]);

  const equipBanner = useCallback(async (bannerId) => {
    if (user) {
      try {
        await axios.post(`${API}/profile/banner/equip`, { banner_id: bannerId }, { withCredentials: true });
        await refreshUser();
        SFX.click?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur");
      }
    } else {
      setGuest((g) => ({ ...g, equipped_banner: bannerId }));
      SFX.click?.();
    }
  }, [user, refreshUser]);

  const value = useMemo(() => ({
    profile,
    grantXP,
    grantMetaGold,
    unlockAchievement,
    isUnlocked,
    updateProfile,
    purchaseBorder,
    equipBorder,
    equipBanner,
    XP_REWARDS,
  }), [profile, grantXP, grantMetaGold, unlockAchievement, isUnlocked, updateProfile, purchaseBorder, equipBorder, equipBanner]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = () => {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
};
